/* ==========================================================================
   Option 3 — AJAX cart (session 16, reworked session 17 for slow connections).

   What it does
   ------------
   1. Product-card "+" and the PDP add button post to /cart/add.js instead of navigating away.
   2. The header cart badge updates instantly (and pops) — no page reload, no second request.
   3. A mini-cart drawer shows what is in the cart, with qty +/- and remove.
   4. Everything degrades: with JS off, the forms are still plain POSTs to /cart/add and the
      cart icon is still a link to /cart. Nothing here is required for the store to work.

   Optimistic by design — this is the whole point of the rework
   ------------------------------------------------------------
   The first version did every visible thing only AFTER the network answered: the badge, the
   confirmation and the button state all waited on /cart/add.js. On a 4G phone in Egypt that
   reads as "I tapped + and nothing happened", so customers tap again — which is also what
   produced the interleaved cart states we saw in testing.

   Now the tap is answered from the device, before a single byte leaves it: the badge moves,
   the button ticks, and the confirmation strip appears — using the card's OWN already-decoded
   image, so even the thumbnail costs no network. The request goes out behind that. If it
   fails, the badge rolls back and the strip becomes the error. Nothing the customer sees is
   ever waiting on the connection.

   Two correctness rules make optimism safe
   ----------------------------------------
   • Mutations are SERIALIZED through one promise chain. Shopify's cart is not concurrency-safe;
     two overlapping writes are how you get "Sorry — we couldn't update your cart".
   • Rendered HTML is applied under a SEQUENCE GUARD. Responses can land out of order, and a
     stale drawer body overwriting a fresh one is what made the drawer disagree with the toast.

   Why the Section Rendering API, and why it stays on /cart/add.js
   ---------------------------------------------------------------
   /cart/add.js returns the LINE ITEM, not the cart — no item_count, no HTML. Passing
   `sections: 'gp-cart-drawer'` makes Shopify return the drawer already rendered by Liquid, so
   money formatting, EN/AR locale and RTL all come from the server. It costs a bigger response,
   but the customer is no longer waiting on it — so one warm-the-drawer request beats two
   smaller ones on a high-latency link, which is the connection we are optimising for.

   Line items are addressed by KEY, not by line index. Indices shift when a line is removed, so
   a queued request could hit the wrong product. Keys are stable.

   Deliberately NOT using Dawn's cart-drawer.js: the option3 sections are written to have no Dawn
   JS dependency, and Dawn's markup/CSS conventions have collided with option3.css three times.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION = 'gp-cart-drawer';
  var routes = (window.routes || {});
  var addUrl = routes.cart_add_url || '/cart/add';
  var changeUrl = routes.cart_change_url || '/cart/change';
  var cartUrl = routes.cart_url || '/cart';

  var drawer = document.getElementById('gp-cart-drawer');
  var badge = document.querySelector('[data-cart-count]');
  var lastFocus = null;
  var toastTimer = null;

  var count = 0;   // what the badge shows; moved optimistically, reconciled from rendered HTML
  var seq = 0;     // increments per request that will render HTML
  var applied = 0; // the newest sequence whose HTML we have applied

  // On the cart page a drawer would cover the thing the customer is looking at — use a toast
  // and refresh the page instead so the real cart stays the source of truth.
  var onCartPage = document.body.getAttribute('data-template') === 'cart' ||
    (document.querySelector('main[data-template]') || {}).dataset?.template === 'cart';

  /* ── strings ────────────────────────────────────────────────────────────
     Shopify's `t` filter HTML-escapes its output, so a locale string with an apostrophe
     arrives as "couldn&#39;t" — and since we set these with textContent (correctly: they must
     never be parsed as markup), the entity was rendering literally on screen. Decode once,
     here, rather than removing apostrophes from the copy. Assigning to a textarea's innerHTML
     is the safe decoder: textarea content is never parsed as markup, so nothing can execute. */
  var decoder = document.createElement('textarea');
  function decodeEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    decoder.innerHTML = s;
    return decoder.value;
  }
  function t(key, fallback) {
    var s = window.gpCartStrings || {};
    return decodeEntities(s[key] || fallback);
  }

  /* ── one writer at a time ───────────────────────────────────────────── */
  var chain = Promise.resolve();
  function queue(fn) {
    var next = chain.then(fn, fn); // run regardless of whether the previous write failed
    chain = next.catch(function () {});
    return next;
  }

  /* ── badge ──────────────────────────────────────────────────────────── */
  function setCount(n) {
    count = n < 0 ? 0 : n;
    if (!badge) return;
    var prev = parseInt(badge.textContent, 10) || 0;
    badge.textContent = count;
    badge.hidden = count <= 0;
    if (count !== prev && count > 0) {
      badge.classList.remove('is-bump');
      void badge.offsetWidth; // restart the animation
      badge.classList.add('is-bump');
    }
    var label = document.querySelector('.cart-btn');
    if (label) {
      label.setAttribute('aria-label', t('cart', 'Cart') + (count > 0 ? ' (' + count + ')' : ''));
    }
  }

  /* ── drawer open/close ──────────────────────────────────────────────── */
  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    hideToast(); // the drawer supersedes the confirmation strip
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var close = drawer.querySelector('[data-gp-cart-close]');
    if (close) setTimeout(function () { close.focus(); }, 60);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function drawerOpen() {
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  /* ── confirmation strip ─────────────────────────────────────────────── */
  function toastEl() {
    var el = document.getElementById('gp-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'gp-toast';
      el.className = 'gp-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    return el;
  }

  function hideToast() {
    var el = document.getElementById('gp-toast');
    if (el) el.classList.remove('is-on');
    clearTimeout(toastTimer);
  }

  function toast(msg) {
    var el = toastEl();
    el.className = 'gp-toast';
    el.textContent = msg;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-on'); }, 3200);
  }

  // Confirmation that does NOT take over the page.
  //
  // The drawer used to open on every add. On mobile the panel is the full viewport, so adding one
  // item looked and felt exactly like being sent to the cart — the opposite of "add and keep
  // exploring". Now: the badge bumps, this strip confirms what went in and offers the cart, and it
  // gets out of the way on its own. The customer opens the cart when THEY decide to.
  function toastAdded(meta) {
    if (drawerOpen()) return; // the open drawer already shows the change; two confirmations is noise
    var el = toastEl();
    el.className = 'gp-toast gp-toast--added';
    el.innerHTML =
      '<span class="gp-toast-img"></span>' +
      '<span class="gp-toast-body">' +
        '<span class="gp-toast-msg"></span>' +
        '<span class="gp-toast-name"></span>' +
      '</span>' +
      '<a class="gp-toast-cta" href="' + cartUrl + '" data-gp-cart-open></a>';

    // Merchant data and locale strings go in as TEXT, never as markup.
    el.querySelector('.gp-toast-msg').textContent = t('added', 'Added to your cart');
    el.querySelector('.gp-toast-cta').textContent = t('view_cart', 'View cart');
    var nameEl = el.querySelector('.gp-toast-name');
    if (meta && meta.title) nameEl.textContent = meta.title; else nameEl.remove();

    var imgWrap = el.querySelector('.gp-toast-img');
    if (meta && meta.src) {
      // Reuse the exact URL the card already painted: it is in the browser cache, so the
      // thumbnail appears with the strip instead of after another round trip.
      var img = document.createElement('img');
      img.src = meta.src;
      img.alt = '';
      img.width = 40;
      img.height = 40;
      imgWrap.appendChild(img);
    } else {
      imgWrap.remove();
    }

    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-on'); }, 4200);
  }

  // What was added, read off the page we are already on — no network, no waiting.
  function localMeta(form) {
    var card = form.closest('.prod');
    var img, name;
    if (card) {
      img = card.querySelector('.prod-img img');
      name = card.querySelector('.prod-name');
    } else {
      img = document.querySelector('.pdp-media img, .pdp-gallery img, .product-media img');
      name = document.querySelector('.pdp-title, h1');
    }
    return {
      src: img ? (img.currentSrc || img.src) : '',
      title: name ? name.textContent.trim() : ''
    };
  }

  /* ── swap the server-rendered drawer body ───────────────────────────── */
  function applySection(html, mySeq) {
    if (!html) return;
    if (mySeq < applied) return; // a newer response already landed — never go backwards
    applied = mySeq;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.getElementById('gp-drawer-body');
    var current = document.getElementById('gp-drawer-body');
    if (fresh && current) {
      current.replaceWith(fresh);
      setCount(parseInt(fresh.getAttribute('data-cart-count'), 10) || 0);
    }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  // Pull the drawer back in line with the server after anything we could not reconcile.
  function resync() {
    var mySeq = ++seq;
    return fetch(cartUrl + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { applySection(data[SECTION], mySeq); })
      .catch(function () {});
  }

  /* ── add to cart ────────────────────────────────────────────────────── */
  function addToCart(form, button) {
    var fd = new FormData(form);
    var id = fd.get('id');
    if (!id) return;
    var qty = Number(fd.get('quantity') || 1) || 1;

    /* ---- answered on the device, before the request is even built ---- */
    setCount(count + qty);
    if (button) {
      button.classList.remove('is-loading');
      button.classList.add('is-done');
      setTimeout(function () { button.classList.remove('is-done'); }, 1200);
    }
    toastAdded(localMeta(form));

    var payload = { items: [{ id: Number(id), quantity: qty }], sections: SECTION };

    // carry line-item properties (none today, but PDP forms may gain them)
    var props = {};
    fd.forEach(function (v, k) {
      var m = k.match(/^properties\[(.+)\]$/);
      if (m) props[m[1]] = v;
    });
    if (Object.keys(props).length) payload.items[0].properties = props;

    var mySeq = ++seq;
    return queue(function () {
      return postJSON(addUrl + '.js', payload)
        .then(function (data) {
          if (onCartPage) {
            window.location.reload(); // on /cart the page itself is the feedback
            return;
          }
          applySection(data.sections && data.sections[SECTION], mySeq);
        })
        .catch(function (err) {
          setCount(count - qty); // take the optimistic bump back
          toast((err && (err.description || err.message)) || t('error', 'Could not add to cart'));
          resync();
        });
    });
  }

  /* ── change quantity / remove ───────────────────────────────────────── */
  function changeLine(key, quantity, itemEl) {
    var was = 0;
    if (itemEl) {
      var qtyEl = itemEl.querySelector('.gpd-qty b');
      was = parseInt(qtyEl && qtyEl.textContent, 10) || 0;
      if (qtyEl) qtyEl.textContent = quantity; // move the number now, reconcile from HTML later
      itemEl.classList.add('is-busy');
      if (Number(quantity) === 0) itemEl.classList.add('is-going');
    }
    setCount(count + (Number(quantity) - was));

    var mySeq = ++seq;
    return queue(function () {
      return postJSON(changeUrl + '.js', { id: key, quantity: Number(quantity), sections: SECTION })
        .then(function (data) {
          applySection(data.sections && data.sections[SECTION], mySeq);
        })
        .catch(function () {
          if (itemEl) itemEl.classList.remove('is-busy', 'is-going');
          toast(t('error', 'Could not update the cart'));
          resync(); // whatever the server actually holds wins
        });
    });
  }

  /* ── wiring (delegated — survives the drawer body being replaced) ───── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('[data-gp-add]')) return;
    e.preventDefault();
    var btn = form.querySelector('button[type="submit"], button[name="add"]') ||
      document.querySelector('button[form="' + form.id + '"]');
    addToCart(form, btn);
  });

  document.addEventListener('click', function (e) {
    // sticky mobile add-to-cart bar posts the PDP form from outside it
    var outside = e.target.closest('button[form][name="add"]');
    if (outside) {
      var owner = document.getElementById(outside.getAttribute('form'));
      if (owner && owner.matches('[data-gp-add]')) {
        e.preventDefault();
        addToCart(owner, outside);
        return;
      }
    }

    if (e.target.closest('[data-gp-cart-open]')) {
      // On /cart the drawer would just cover the same contents — let the link navigate/stay put.
      if (onCartPage) return;
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-gp-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var qtyBtn = e.target.closest('[data-gp-key]');
    if (qtyBtn) {
      e.preventDefault();
      var itemEl = qtyBtn.closest('.gpd-item');
      var to;
      if (qtyBtn.hasAttribute('data-gp-step')) {
        // Derive the target from the number ON SCREEN, not from an attribute the server stamped:
        // a second tap arrives before any re-render, and the stamped value would still say "2".
        var shown = parseInt((itemEl.querySelector('.gpd-qty b') || {}).textContent, 10);
        if (isNaN(shown)) return;
        to = Math.max(0, shown + Number(qtyBtn.getAttribute('data-gp-step')));
      } else {
        to = Number(qtyBtn.getAttribute('data-gp-to')); // remove
      }
      changeLine(qtyBtn.getAttribute('data-gp-key'), to, itemEl);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerOpen()) closeDrawer();
  });

  // keep focus inside the open drawer
  document.addEventListener('focusin', function (e) {
    if (!drawerOpen()) return;
    if (!drawer.contains(e.target)) {
      var first = drawer.querySelector('[data-gp-cart-close]');
      if (first) first.focus();
    }
  });

  // Seed the badge from the server-rendered drawer so a cached page never shows a stale count.
  var seed = document.getElementById('gp-drawer-body');
  if (seed) setCount(parseInt(seed.getAttribute('data-cart-count'), 10) || 0);
})();
